import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ColumnPinningState,
  OnChangeFn,
  VisibilityState,
} from '@tanstack/react-table';

export interface ManageableColumnConfig {
  id: string;
  defaultVisible?: boolean;
  defaultPinned?: 'left' | 'right' | false;
}

interface UseColumnPreferencesArgs {
  /** Identifies the table, e.g. 'studies', 'sites' — used in the storage key. */
  tableId: string;
  /** Signed-in user's id, so preferences don't leak across users on a shared machine. */
  userId: string | undefined;
  columns: ManageableColumnConfig[];
  defaultOrder: string[];
}

interface StoredPreferences {
  version: 1;
  columnVisibility: VisibilityState;
  columnOrder: string[];
  columnPinning: ColumnPinningState;
}

const STORAGE_VERSION = 2;

const buildDefaults = (
  columns: ManageableColumnConfig[],
  defaultOrder: string[]
): StoredPreferences => {
  const columnVisibility: VisibilityState = {};
  const columnPinning: ColumnPinningState = { left: [], right: [] };

  columns.forEach(column => {
    if (column.defaultVisible === false) {
      columnVisibility[column.id] = false;
    }
    if (column.defaultPinned === 'left') {
      columnPinning.left?.push(column.id);
    } else if (column.defaultPinned === 'right') {
      columnPinning.right?.push(column.id);
    }
  });

  return {
    version: STORAGE_VERSION,
    columnVisibility,
    columnOrder: [...defaultOrder],
    columnPinning,
  };
};

const storageKey = (tableId: string, userId: string) =>
  `column-prefs:${userId}:${tableId}`;

const readStoredPreferences = (
  tableId: string,
  userId: string,
  columns: ManageableColumnConfig[]
): StoredPreferences | null => {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(storageKey(tableId, userId));
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<StoredPreferences>;
    if (parsed.version !== STORAGE_VERSION) return null;

    const knownIds = new Set(columns.map(c => c.id));
    const storedOrder = (parsed.columnOrder ?? []).filter(id =>
      knownIds.has(id)
    );
    // Columns added to `columns` after a user last saved preferences won't be
    // in their stored order yet — append them (in config order) so they stay
    // reachable in the Column Manager instead of disappearing forever.
    const storedOrderSet = new Set(storedOrder);
    const newIds = columns.map(c => c.id).filter(id => !storedOrderSet.has(id));
    const columnOrder = [...storedOrder, ...newIds];
    const configById = new Map(columns.map(c => [c.id, c]));
    const columnVisibility: VisibilityState = {};
    Object.entries(parsed.columnVisibility ?? {}).forEach(([id, value]) => {
      if (knownIds.has(id)) columnVisibility[id] = value;
    });
    const columnPinning: ColumnPinningState = {
      left: (parsed.columnPinning?.left ?? []).filter(id => knownIds.has(id)),
      right: (parsed.columnPinning?.right ?? []).filter(id => knownIds.has(id)),
    };
    // A newly-added column has no stored visibility/pinning entry yet — seed
    // it from its own config default rather than implicitly defaulting to
    // "visible, unpinned" regardless of what the column declares.
    newIds.forEach(id => {
      const config = configById.get(id);
      if (config?.defaultVisible === false) columnVisibility[id] = false;
      if (config?.defaultPinned === 'left') columnPinning.left?.push(id);
      if (config?.defaultPinned === 'right') columnPinning.right?.push(id);
    });

    return {
      version: STORAGE_VERSION,
      columnVisibility,
      columnOrder,
      columnPinning,
    };
  } catch {
    return null;
  }
};

const writeStoredPreferences = (
  tableId: string,
  userId: string,
  prefs: StoredPreferences
) => {
  try {
    window.localStorage.setItem(
      storageKey(tableId, userId),
      JSON.stringify(prefs)
    );
  } catch {
    // Storage unavailable (private browsing, quota) — preferences just won't persist.
  }
};

export const useColumnPreferences = ({
  tableId,
  userId,
  columns,
  defaultOrder,
}: UseColumnPreferencesArgs) => {
  const [state, setState] = useState<StoredPreferences>(() => {
    if (!userId) return buildDefaults(columns, defaultOrder);
    return (
      readStoredPreferences(tableId, userId, columns) ??
      buildDefaults(columns, defaultOrder)
    );
  });
  // Tracks whether the user has changed a preference since mount. Routes can
  // render before /users/me resolves (ProtectedRoute doesn't block on
  // `loading`), so a user can toggle a column before `userId` is known; once
  // that happens, the re-hydration effect below must not clobber it with
  // whatever was on disk before the toggle.
  const userModifiedRef = useRef(false);

  // Re-hydrate once userId becomes available (e.g. /users/me resolves after mount).
  useEffect(() => {
    if (!userId || userModifiedRef.current) return;
    const stored = readStoredPreferences(tableId, userId, columns);
    if (stored) setState(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, tableId]);

  const persist = useCallback(
    (next: StoredPreferences) => {
      setState(next);
      if (userId) writeStoredPreferences(tableId, userId, next);
    },
    [tableId, userId]
  );

  const setColumnVisibility: OnChangeFn<VisibilityState> = useCallback(
    updater => {
      userModifiedRef.current = true;
      setState(prev => {
        const columnVisibility =
          typeof updater === 'function'
            ? updater(prev.columnVisibility)
            : updater;
        const next = { ...prev, columnVisibility };
        if (userId) writeStoredPreferences(tableId, userId, next);
        return next;
      });
    },
    [tableId, userId]
  );

  const setColumnOrder: OnChangeFn<string[]> = useCallback(
    updater => {
      userModifiedRef.current = true;
      setState(prev => {
        const columnOrder =
          typeof updater === 'function' ? updater(prev.columnOrder) : updater;
        const next = { ...prev, columnOrder };
        if (userId) writeStoredPreferences(tableId, userId, next);
        return next;
      });
    },
    [tableId, userId]
  );

  const setColumnPinning: OnChangeFn<ColumnPinningState> = useCallback(
    updater => {
      userModifiedRef.current = true;
      setState(prev => {
        const columnPinning =
          typeof updater === 'function' ? updater(prev.columnPinning) : updater;
        const next = { ...prev, columnPinning };
        if (userId) writeStoredPreferences(tableId, userId, next);
        return next;
      });
    },
    [tableId, userId]
  );

  const resetToDefaults = useCallback(() => {
    persist(buildDefaults(columns, defaultOrder));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persist]);

  return {
    columnVisibility: state.columnVisibility,
    columnOrder: state.columnOrder,
    columnPinning: state.columnPinning,
    setColumnVisibility,
    setColumnOrder,
    setColumnPinning,
    resetToDefaults,
  };
};
